/**
 * Feed Halaman Alert (ADR-0055 Fase 3) — logika MURNI, teruji di Node.
 *
 * Menggabungkan kejadian alert (`GET /alert-events`) dengan nama alat (`GET /assets`) dan — bila
 * boleh — aturan (`GET /alert-rules`) untuk severity, metrik, dan ambang yang memicunya. Kejadian
 * sendiri TIDAK memuat metrik/severity (keduanya milik aturan lewat `alertRuleId`); kejadian
 * `offline` tak punya aturan. Tanpa akses aturan, baris tetap tampil dengan kind + nilai saja
 * (degradasi aman), bukan hilang.
 */
import type {
  AlertEventResponse,
  AlertOperator,
  AlertRuleResponse,
  AlertSeverity,
  AssetResponse,
} from '../api/types';

export type FilterAlert = 'semua' | 'terbuka' | 'ambang' | 'offline';

export interface BarisAlert {
  readonly id: string;
  readonly assetId: string;
  readonly namaAlat: string;
  readonly kind: 'threshold' | 'offline';
  readonly severity: AlertSeverity | null;
  readonly metricKey: string | null;
  readonly operator: AlertOperator | null;
  readonly threshold: number | null;
  readonly value: number | null;
  readonly firedAt: string;
  readonly resolvedAt: string | null;
}

/** Peta id-alat → nama, untuk menampilkan nama alih-alih UUID. */
export function namaAlatDari(assets: readonly AssetResponse[]): ReadonlyMap<string, string> {
  return new Map(assets.map((a) => [a.id, a.name]));
}

/** Peta id-aturan → aturan, untuk mengayakan kejadian ambang. */
export function aturanDari(
  rules: readonly AlertRuleResponse[],
): ReadonlyMap<string, AlertRuleResponse> {
  return new Map(rules.map((r) => [r.id, r]));
}

/**
 * Bangun baris feed terurut dari kejadian: terbaru dulu (firedAt desc), pemutus seri `id` agar
 * urutan deterministik (penting untuk uji & render stabil). Nama alat tak dikenal → fallback ke
 * potongan id, bukan string kosong (baris tetap dapat dikenali).
 */
export function bangunFeed(
  events: readonly AlertEventResponse[],
  namaAlat: ReadonlyMap<string, string>,
  aturanById: ReadonlyMap<string, AlertRuleResponse>,
): readonly BarisAlert[] {
  return events
    .map((e): BarisAlert => {
      const rule = e.alertRuleId !== null ? (aturanById.get(e.alertRuleId) ?? null) : null;
      return {
        id: e.id,
        assetId: e.assetId,
        namaAlat: namaAlat.get(e.assetId) ?? `Alat ${e.assetId.slice(0, 8)}`,
        kind: e.kind,
        severity: rule?.severity ?? null,
        metricKey: rule?.metricKey ?? null,
        operator: rule?.operator ?? null,
        threshold: rule?.threshold ?? null,
        value: e.value,
        firedAt: e.firedAt,
        resolvedAt: e.resolvedAt,
      };
    })
    .slice()
    .sort((a, b) => {
      const d = Date.parse(b.firedAt) - Date.parse(a.firedAt);
      return d !== 0 ? d : a.id.localeCompare(b.id);
    });
}

/** Saring feed sesuai pilihan. 'terbuka' = belum pulih; 'ambang'/'offline' per jenis. */
export function saringFeed(
  rows: readonly BarisAlert[],
  filter: FilterAlert,
): readonly BarisAlert[] {
  switch (filter) {
    case 'terbuka':
      return rows.filter((r) => r.resolvedAt === null);
    case 'ambang':
      return rows.filter((r) => r.kind === 'threshold');
    case 'offline':
      return rows.filter((r) => r.kind === 'offline');
    default:
      return rows;
  }
}

/** Angka untuk tile ringkas. `pulih24j` dihitung relatif terhadap `now` yang dioper. */
export function ringkasFeed(
  rows: readonly BarisAlert[],
  now: Date,
): { readonly terbuka: number; readonly offline: number; readonly pulih24j: number } {
  const batas = now.getTime() - 24 * 60 * 60 * 1000;
  let terbuka = 0;
  let offline = 0;
  let pulih24j = 0;
  for (const r of rows) {
    if (r.resolvedAt === null) {
      terbuka += 1;
      if (r.kind === 'offline') offline += 1;
    } else {
      const t = Date.parse(r.resolvedAt);
      if (!Number.isNaN(t) && t >= batas) pulih24j += 1;
    }
  }
  return { terbuka, offline, pulih24j };
}
